import { useNavigate } from 'react-router-dom'

import { Wizard, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { ProcurementMyPurchaseRequestsDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  CreatePurchaseRequestDocument,
  CurrencySelectorCurrencyFragment,
  ProcurementTypeItemFragment,
} from 'generated/resources/graphql'
import { FormElementWithLocaleFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { useActivities } from 'modules/core/lib/activities/provider'
import { convertDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import AttachmentsStep from './Steps/AttachmentsStep'
import CustomFormSectionStep from './Steps/CustomFormSectionStep'
import GeneralInformationStep from './Steps/GeneralInformationStep'
import { WizardData } from './types'

interface Props {
  procurementType: ProcurementTypeItemFragment | null
  currencies: CurrencySelectorCurrencyFragment[]
  defaultCurrency?: string
  hasCustomFormsEnabled: boolean
  onClose?: () => void
  isPreviewMode?: boolean
}

const convertDateToBackendFormat = (contractVersionDeadline: string) => {
  if (!contractVersionDeadline) return undefined

  return convertDate(contractVersionDeadline, 'ui', 'backend')
}

export const NewPurchaseRequestModal: React.FC<Props> = ({
  procurementType,
  currencies,
  defaultCurrency,
  hasCustomFormsEnabled,
  onClose = null,
  isPreviewMode = false,
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { company, employee } = useCurrentSession()

  const { currentActivity } = useActivities()

  const [createPurchaseRequest] = useMutation(CreatePurchaseRequestDocument, {
    extractorFn: (data) => data.procurementMutations.createProcurementPurchaseRequest,
    refetchQueries: [ProcurementMyPurchaseRequestsDatatableDocument],
  })

  if (!procurementType) {
    return null
  }

  const { id } = procurementType
  const form = procurementType.formsConnection.nodes[0]
  const formElements = form?.elements ?? []

  const handleSubmit = async (data: WizardData) => {
    app.setProgress({
      message: i18n.t('procurement.purchase_requests.new_request_wizard.notifications.creating'),
    })

    const answers = Object.values(data.formAnswer).map((answer) => ({
      answer: answer.answer,
      comment: answer.comment,
      elementId: answer.elementId,
      elementOptionIds: answer.elementOptionIds,
    }))

    const result = await createPurchaseRequest({
      variables: {
        description: data.description,
        typeId: id,
        amount: Number(data.cost) ? Number(data.cost) * 100 : 0,
        currency: data.currency,
        url: data.url,
        deadline: convertDateToBackendFormat(data.deadline || ''),
        additionalInformation: data.additionalInformation,
        date: new Date().toISOString(),
        files: data.files,
        companyId: company.id,
        requesterEmployeeId: employee.id,
        vendorId: data.vendor?.id,
        answers,
        formId: form?.id,
      },
    })
    app.setProgress(null)

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.new_request_wizard.notifications.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.new_request_wizard.notifications.success'),
      })

      track(trackingEvents.procurement.submitNewPurchaseRequest, {
        description: data.description,
        cost: data.cost,
        currency: data.currency,
        deadline: data.deadline ? 'yes' : 'no',
        URL: data.url ? 'yes' : 'no',
        additionalInformation: data.additionalInformation,
        // TODO: This will be always no, we are creating a new vendor in activity
        vendorNew: data.vendor?.id == 0 ? 'yes' : 'no',
        vendorTaxId: data.vendor?.taxId ?? '',
        attachments: data.files.length !== 0 ? 'yes' : 'no',
      })

      handleClose()
    }
  }

  const handleClose = () => {
    onClose ? onClose() : navigate(navigation.myPurchases.fullPath())
  }

  const isGeneralInfoCompleted = (data: WizardData): boolean => {
    return isPreviewMode ? true : !!data.description && !!data.currency
  }

  const isAttachmentsInfoCompleted = (): boolean => {
    return !isPreviewMode
  }

  const isFormDataCompleted = (
    data: WizardData,
    elements: FormElementWithLocaleFragment[]
  ): boolean => {
    return isPreviewMode
      ? true
      : elements.every((element) => {
          if (!element.mandatory) return true
          const answerData = data.formAnswer[element.id]
          return answerData !== undefined && answerData !== null && answerData.answer !== ''
        })
  }

  // Generate dynamic steps
  const generateSteps = () => {
    const steps = []

    // Step 1: Purchase Information is always present (Purchase request default info)
    steps.push({
      title: i18n.t(
        'procurement.purchase_requests.new_request_wizard.steps.general_information.title'
      ),
      nextText: i18n.t(
        'procurement.purchase_requests.new_request_wizard.steps.general_information.next'
      ),
      render: (props: WizardStepRenderParams<WizardData>) => (
        <GeneralInformationStep
          {...props}
          defaultCurrency={initialData.currency}
          currencies={currencies}
        />
      ),
      isCompleted: isGeneralInfoCompleted,
    })

    if (hasCustomFormsEnabled) {
      // Building the steps dynamically. We create one step per section and a single step for the elements that don't belong to a section
      // Skip locked sections. We asume locked section = default section.
      const sections = formElements.filter((el) => el.type === 'section' && !el.params?.locked)
      const elementsWithoutSection = formElements.filter(
        (el) => el.type !== 'section' && !el.sectionId
      )

      if (sections.length > 0) {
        sections.forEach((section) => {
          const sectionElements = formElements.filter((el) => el.sectionId === section.id)
          steps.push({
            title: section.label,
            nextText: i18n.t(
              'procurement.purchase_requests.new_request_wizard.steps.custom_form_section_step.next'
            ),
            render: (props: WizardStepRenderParams<WizardData>) => (
              <CustomFormSectionStep
                {...props}
                elements={sectionElements}
                sectionTitle={section.label}
                sectionDescription={section.description}
              />
            ),
            isCompleted: (data: WizardData) => isFormDataCompleted(data, sectionElements),
          })
        })
      }

      if (elementsWithoutSection.length > 0) {
        steps.push({
          title: i18n.t(
            'procurement.purchase_requests.new_request_wizard.steps.additional_information_step.title'
          ),
          nextText: i18n.t(
            'procurement.purchase_requests.new_request_wizard.steps.additional_information_step.next'
          ),
          render: (props: WizardStepRenderParams<WizardData>) => (
            <CustomFormSectionStep
              {...props}
              elements={elementsWithoutSection}
              sectionTitle={i18n.t(
                'procurement.purchase_requests.new_request_wizard.steps.additional_information_step.title'
              )}
              sectionDescription={i18n.t(
                'procurement.purchase_requests.new_request_wizard.steps.additional_information_step.description'
              )}
            />
          ),
          isCompleted: (data: WizardData) => isFormDataCompleted(data, elementsWithoutSection),
        })
      }
    }

    // Final step: Attachments (always present)
    steps.push({
      title: i18n.t('procurement.purchase_requests.new_request_wizard.steps.attachments.title'),
      nextText: i18n.t('procurement.purchase_requests.new_request_wizard.steps.attachments.next'),
      render: (props: WizardStepRenderParams<WizardData>) => <AttachmentsStep {...props} />,
      isCompleted: isAttachmentsInfoCompleted,
    })

    return steps
  }
  const initialData = {
    description: '',
    cost: '',
    currency: defaultCurrency || '',
    url: undefined,
    deadline: undefined,
    additionalInformation: undefined,
    vendor: undefined,
    files: [],
    formAnswer: {},
  }

  return (
    <Wizard.Modal<WizardData>
      initialData={initialData}
      icon="document-edit"
      locked={!!currentActivity}
      title={
        isPreviewMode
          ? i18n.t('procurement.purchase_requests.new_request_wizard.title_preview')
          : i18n.t('procurement.purchase_requests.new_request_wizard.title')
      }
      onClose={handleClose}
      onSubmit={handleSubmit}
      steps={generateSteps()}
    />
  )
}
